
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import ChecklistFieldModule from '@/components/views/productView/ChecklistFieldModule.vue';
import MultipositionHeader from '@/components/global/popup/MultipositionHeader.vue';
import ProductCombination from '@/components/views/productView/ProductCombination.vue';
import CombinationsSelectorPopup from '@/components/global/popup/CombinationsSelectorPopup.vue';
import CombinationsEditPopup from '@/components/global/popup/CombinationsEditPopup.vue';
import ProductSlotRow from '@/components/global/popup/ProductSlotRow.vue';
import { MultipositionDeleteMap } from '@/interfaces/general/MultipositionDeleteMap';
import { ProductRowItem } from '@/interfaces/general/ProductRowItem';
import { ProductCombinations } from '@/interfaces/components/projectNew/ProductCombinations';
import { RadioButtonGroupOptions } from '@/interfaces/components/RadioButtonGroupOptions';
import { debounce } from 'vue-debounce';
import ConfiguratorService from '@/services/ConfiguratorService';
import { Action } from 'vuex-class';
import { UpdateProductFormEntry } from '@/interfaces/components/configurator/UpdateProductFormEntry';
import {
    createNewRowsWithPastedData,
    determineIfSingleValueIsPasted,
    extractCellsAsArraysFromString,
    updateExistingRowsWithPastedData,
} from '@/helpers/FormDataHelper';
import { ChecklistFieldEntry } from '@/interfaces/components/configurator/ChecklistFieldEntry';
import { Tabs } from 'ant-design-vue';
import OfferRepository from '@/repositories/OfferRepository';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { setRowColor } from '@/helpers/FormDataHelper';
import { CustomColors } from '@/enums/global/CustomColors';
import { combinationsConfiguration } from '@/modules/ConfiguratorCombinations/helpers/CombinationsConfiguration';
import { normalizeSegmentName } from '@/helpers/SegmentNameMapping';
import OfferItem from '@/models/OfferItem';

let globalCombinationCounter = 0;
// import { normalizeSegmentName } from '@/helpers/SegmentNameMapping';
import { jointsConfiguration } from '@/modules/ConfiguratorJoints/helpers/JointsConfiguration';

@Component({
    name: 'SegmentedMultiposition',
    components: {
        ChecklistFieldModule,
        MultipositionHeader,
        ProductSlotRow,
        Tabs,
        TabPane: Tabs.TabPane,
        ProductCombination,
        CombinationsSelectorPopup,
        CombinationsEditPopup,
    },
})
export default class SegmentedMultiposition extends Vue {
    @Action('configurator/updateActiveProductFormValue')
    private updateActiveProductFormValue!: ({ pId, value, productFormId }: UpdateProductFormEntry) => void;

    @Prop({ required: true }) private deleteExistingRow!: (multipositionDeleteMap: MultipositionDeleteMap) => void;
    @Prop({ default: null }) private addNewRow!: (
        n: number,
        existingProductValues: ChecklistFieldEntry[],
    ) => void | null;
    @Prop({ default: null }) private addNewRowInEditMode!: (
        firstActiveItemRow: ProductRowItem,
        existingProductValues: ChecklistFieldEntry[],
    ) => void | null;
    @Prop({ default: null }) private updateItemRowsIsUpdatingState!: (itemRowIndex: number, newState: boolean) => void;
    @Prop({ required: true }) private isUpdatingData!: boolean;
    @Prop({ default: false }) private isAddingProducts!: boolean;
    @Prop({ default: false }) private isCustomProduct!: boolean;
    @Prop({ required: true }) private activeItemRows!: ProductRowItem[];
    @Prop({ default: 1 }) private numberOfProductForms!: number;
    @Prop({ required: true }) private offerId!: string;
    @Prop({ required: true }) private availableSegments!: RadioButtonGroupOptions[];
    @Prop({ required: false, default: false }) private isWindowCombinationWizard!: boolean;
    @Prop({ required: false, default: false }) private productDetails!: any;
    @Prop({ required: false, default: null }) private currentUserGroup!: string | { id: string; name: string };

    private selectedSegment: string | null = null;
    private hiddenIndices: boolean[] = [];
    private level: number = 2;
    private selectedCombinationRows: ProductRowItem[] = [];
    private combinationsSelectorActive: boolean = false;
    private combinationsConfiguratorSelectorActive: boolean = false;
    private count: number = 0;
    private penultimateSelectedLevel = 0;
    private combinationImages: Record<string | number, { image: string; type: 'sestav' | 'kotni' }> = {};

    private get showCombinations() {
        const currentSegment = this.availableSegments.find((seg) => seg.value === this.selectedSegment);
        return currentSegment?.isLocked;
    }

    private get isUsingCombinationsConfigurator() {
        if (!this.selectedSegment) {
            return false;
        }

        const currentConfig = this.currentCombinationsConfiguration;

        if (!currentConfig) {
            return false;
        }

        // We show for every tab now
        return true;

        // Normalize both segment names before comparison to handle different languages
        // return normalizeSegmentName(currentConfig.segment) === normalizeSegmentName(this.selectedSegment);
    }

    private get currentCombinationsConfiguration() {
        if (!this.selectedSegment) {
            return null;
        }

        const itemRow = this.activeItemRows[0];

        // Find configuration that matches the current segment
        const segmentConfig = combinationsConfiguration.find(
            (config) =>
                config.products.includes(this.productDetails.productSystemName) ||
                config.products.includes(itemRow?.offerItem?.name || ''),
        );

        return segmentConfig || null;
    }

    private get currentJointsConfiguration() {
        if (!this.selectedSegment) {
            return null;
        }

        const itemRow = this.activeItemRows[0];

        // Find configuration that matches the current segment
        const segmentConfig = jointsConfiguration.find(
            (config) =>
                config.products.includes(this.productDetails.productSystemName) ||
                config.products.includes(itemRow?.offerItem?.name || ''),
        );

        return segmentConfig || null;
    }

    private get isUsingStandardCombinationsConfigurator() {
        if (!this.isUsingCombinationsConfigurator) {
            return false;
        }

        const currentConfig = this.currentCombinationsConfiguration;

        return currentConfig?.configuratorType === 'standard';
    }

    private get isUsingAlternativeCombinationsConfigurator() {
        if (!this.isUsingCombinationsConfigurator) {
            return false;
        }

        const currentConfig = this.currentCombinationsConfiguration;

        return currentConfig?.configuratorType !== 'standard';
    }

    private get isUsingJointsConfigurator() {
        const currentConfig = this.currentJointsConfiguration;

        return !!currentConfig?.configuratorType;
    }

    private get productCombinations() {
        const combinations: ProductCombinations = {};

        for (const row of this.activeItemRows) {
            if (row.connection === null) {
                continue;
            }

            if (!combinations.hasOwnProperty(row.connection)) {
                this.$set(combinations, row.connection, []);
            }

            combinations[row.connection].push(row);
        }

        return combinations;
    }

    private get rowsWithNoConnection() {
        return this.activeItemRows.filter((row) => {
            return row.connection === null;
        });
    }

    private get manualCombinationsButtonText() {
        if (!this.isUsingStandardCombinationsConfigurator) {
            return this.$t('Add combination') as string;
        }

        return this.$t('Add sestav manually') as string;
    }

    private removedConnectionsByGroupKey: Record<string, number> = {};

    private getRowGroupKey(rows: ProductRowItem[]): string {
        return rows
            .map((row) => (row.offerItem ? `oi:${row.offerItem.id}` : `pf:${row.activeProductFormId}`))
            .sort()
            .join('|');
    }

    private onRemoveCombinationConnection(rows: ProductRowItem[]) {
        const connectionId = rows[0]?.connection ?? null;
        if (connectionId !== null) {
            const groupKey = this.getRowGroupKey(rows);
            this.removedConnectionsByGroupKey[groupKey] = connectionId;
        }
        rows.forEach((row: ProductRowItem) => {
            row.connection = null;
            row.color = '';
        });

        this.$notification.success({
            message: this.$t('Connection removed!') as string,
            description: '',
        });
    }

    private onEditCombination(rows: ProductRowItem[]) {
        this.selectedCombinationRows = rows;
        EventBus.$emit(EventBusEvents.openCombinationsEditPopup);
    }

    private openCombinationsSelector() {
        EventBus.$emit(EventBusEvents.openCombinationsSelectorPopup);
        this.combinationsSelectorActive = true;
    }

    public openCombinationsConfiguratorSelector() {
        EventBus.$emit(EventBusEvents.openCombinationsConfiguratorSelectorPopup, {
            rows: this.activeItemRows,
            productName: this.productDetails.productSystemName,
        });
        this.combinationsConfiguratorSelectorActive = true;
    }

    public openJointsConfiguratorSelector() {
        EventBus.$emit(EventBusEvents.openJointsConfiguratorSelectorPopup, {
            rows: this.activeItemRows,
            productName: this.productDetails.productSystemName,
        });
    }

    private resetCombinationsSelectorStatus() {
        this.combinationsSelectorActive = false;
    }

    private get productCombinationPayload() {
        return {
            hiddenIndices: this.hiddenIndices,
            isLoading: this.isUpdatingData,
            offer: this.offer,
            isCustomProduct: this.isCustomProduct,
            level: this.level,
            deleteExistingRow: this.deleteExistingRow,
            isEditMode: false,
            isUpdating: true,
            selectedSegment: this.selectedSegment,
            updateItemRowsIsUpdatingState: this.updateItemRowsIsUpdatingState,
        };
    }

    private addNewCombination(productFormIds: number[], connectionId?: number) {
        const colors: Record<string | number, any> = { ...CustomColors };

        const selectedRows = this.activeItemRows.filter((rowItem: ProductRowItem) =>
            productFormIds.includes(rowItem.activeProductFormId),
        );

        if (connectionId === undefined) {
            const groupKey = this.getRowGroupKey(selectedRows);
            if (this.removedConnectionsByGroupKey[groupKey] !== undefined) {
                connectionId = this.removedConnectionsByGroupKey[groupKey];
                delete this.removedConnectionsByGroupKey[groupKey];
            } else {
                globalCombinationCounter += 1;
                connectionId = globalCombinationCounter;
            }
        }

        selectedRows.forEach((selectedRowItem) => {
            selectedRowItem.connection = connectionId!;
        });

        if (this.isAddingProducts) {
            this.activeItemRows.forEach((productRowItem) => {
                setRowColor(productRowItem, colors);
            });
        }
    }

    private onConfiguratorCombinationEvent(
        rows: ProductRowItem[],
        addConnection?: boolean,
        combinationImageBase64?: string | null,
        productSystemName?: string,
        type: 'sestav' | 'kotni' = 'sestav',
    ) {
        if (productSystemName && productSystemName !== this.productDetails.productSystemName) {
            return;
        }
        this.updateCombinationFromConfigurator(rows, addConnection, combinationImageBase64, type);
    }

    private debouncedUpdateHiddenIndicesInMultiposition = debounce(() => {
        this.updateHiddenIndicesInMultiposition();
    }, 200);

    private debouncedUpdateHiddenIndices = debounce(() => {
        this.updateHiddenIndices();
    }, 200);

    private get allSegments() {
        return [
            {
                label: this.$t('Svi'),
                value: 'All',
            },
            ...this.availableSegments,
        ];
    }

    private get offer() {
        if (this.offerId == null) {
            return null;
        }

        return OfferRepository.getOfferById(this.offerId);
    }

    public switchLevel(level: number) {
        this.level = -1;
        this.$nextTick(() => {
            this.level = level;

            if (!this.showCombinations || !this.isWindowCombinationWizard) {
                this.penultimateSelectedLevel = level;
            }
        });
    }

    private handleConnection(e: number) {
        this.activeItemRows.forEach((row) => {
            row.connection = e;
        });
    }

    private handleProduction(e: boolean) {
        this.activeItemRows.forEach((row) => {
            row.production = e;
        });
    }

    private async updateHiddenIndices() {
        if (
            this.selectedSegment == null ||
            (!Object.keys(this.productCombinations).length && this.showCombinations && !this.combinationsSelectorActive)
        ) {
            return;
        }

        let productRowRefs: any[];

        // In case product combinations are used, we dig for refs inside child component
        if (this.showCombinations && !this.combinationsSelectorActive) {
            await this.$nextTick();
            productRowRefs = (this.$refs?.productCombination as ProductCombination[])
                ?.map((combinationRef: ProductCombination) => {
                    return combinationRef.$refs.productSlotRow as ProductSlotRow[];
                })
                .flat();
        } else if (this.showCombinations && this.combinationsSelectorActive) {
            await this.$nextTick();
            productRowRefs = (
                ((this.$refs?.combinationsSelector as any)?.$refs.productCombination as ProductCombination).$refs
                    .productSlotRow as ProductSlotRow[]
            ).flat();
        } else {
            productRowRefs = this.$refs.productSlotRow as ProductSlotRow[];
        }

        const allDisabledStates = ConfiguratorService.getAllDisabledStatesFromRow(productRowRefs);

        const firstRow = ConfiguratorService.getInitialColumnVisibilityBasedOnSelectedSegment(
            this.activeItemRows[0],
            this.selectedSegment,
        );

        this.hiddenIndices = ConfiguratorService.determineColumnVisibility(firstRow, allDisabledStates);
    }

    private updateHiddenIndicesInMultiposition() {
        this.updateHiddenIndices();
        this.updateWidthOfProductSlotWrappers();
    }

    private updateWidthOfProductSlotWrappers() {
        // in case of combinations, its updated in a separate component
        if (this.showCombinations) {
            EventBus.$emit(EventBusEvents.updateWidthOfProductSlotWrappers);
            return;
        }
        // needs to be updated if there are significant width changes in the DOM
        // otherwise, the sticky columns won't work properly
        this.$nextTick(async () => {
            let wrapperElements;

            wrapperElements = [
                ...(this.$refs.productSlotRow as ProductSlotRow[]).map(
                    (productSlotRow) => productSlotRow.$refs.wrapper,
                ),
                (this.$refs.multipositionHeader as MultipositionHeader).$refs.wrapper,
            ] as HTMLElement[];

            for await (const wrapperElement of wrapperElements) {
                wrapperElement.style.width = 'auto';
                await this.$nextTick();
                wrapperElement.style.width = `${wrapperElement.scrollWidth}px`;
            }
        });
    }

    private radioButtonChange(segment: string) {
        this.selectedSegment = segment;
    }

    private onPaste(evt: any) {
        if (evt.clipboardData == null || this.isUpdatingData || this.isCustomProduct) {
            return;
        }

        let cells = extractCellsAsArraysFromString(evt.clipboardData.getData('text'));

        if (determineIfSingleValueIsPasted(cells)) {
            evt.preventDefault();
        } else {
            return;
        }

        const productSlotRowElement = evt.target.closest('.c-add-product-row__wrapper');
        const productSlotRowNumber = productSlotRowElement ? productSlotRowElement.dataset.rowNumber : null;
        if (cells.length > 50) {
            cells = cells.slice(0, 50);
            this.$notification.warn({
                message: (this.$t('Započeto kopiranje __NUMBEROFITEMS__ pozicija') as string).replace(
                    '__NUMBEROFITEMS__',
                    String(cells.length),
                ),
                // tslint:disable-next-line:max-line-length
                description: this.$t(
                    'Limit zaljepljenih proizvoda je 50 stoga će se zaljepiti samo prvih 50 proizvoda',
                ) as string,
            });
        } else {
            this.$notification.info({
                message: (this.$t('Započeto kopiranje __NUMBEROFITEMS__ pozicija') as string).replace(
                    '__NUMBEROFITEMS__',
                    String(cells.length),
                ),
                description: this.$t('Ova akcija može potrajati neko vrijeme stoga molimo za strpljenje') as string,
            });
        }

        const widthElement = productSlotRowElement
            .querySelector('[data-test-id="p7402"]')
            ?.closest('.is-multiposition') as HTMLElement;

        const heightElement = productSlotRowElement
            .querySelector('[data-test-id="p7403"]')
            ?.closest('.is-multiposition') as HTMLElement;

        const isAlternativeFormForPasting =
            !widthElement.classList.contains('is-multiposition--visible') &&
            !heightElement.classList.contains('is-multiposition--visible');

        updateExistingRowsWithPastedData(this.activeItemRows, cells, productSlotRowNumber, isAlternativeFormForPasting);
        createNewRowsWithPastedData(
            cells,
            this.activeItemRows,
            this.addNewRow,
            this.addNewRowInEditMode,
            productSlotRowNumber,
            isAlternativeFormForPasting,
        );
    }

    private setConfiguratorLevel() {
        let isExpertUG = false;

        // Allow expert level for specific user groups using id or name and match by id or name for edge cases
        const expertUserGroups = ['1', '2', '3'];
        const expertUGNames = ['ALUBOMA'];

        if (typeof this.currentUserGroup === 'string') {
            isExpertUG = expertUserGroups.includes(this.currentUserGroup);
        } else if (this.currentUserGroup && typeof this.currentUserGroup === 'object') {
            isExpertUG =
                expertUserGroups.includes(String(this.currentUserGroup.id)) ||
                expertUGNames.includes(String(this.currentUserGroup.name));
        }

        if (isExpertUG) {
            this.level = 2;
        } else {
            this.level = 0;
        }

        this.penultimateSelectedLevel = this.level;
    }

    public getCombinationImages(): Record<string | number, { image: string; type: 'sestav' | 'kotni' }> {
        return this.combinationImages;
    }

    private async updateCombinationFromConfigurator(
        selectedRows: ProductRowItem[],
        addConnection: boolean = true,
        combinationImageBase64: string | null = null,
        type: 'sestav' | 'kotni' = 'sestav',
    ) {
        const selectedIds = new Set(selectedRows.map((r) => r.activeProductFormId));
        const matchingActiveRows = this.activeItemRows.filter((r) => selectedIds.has(r.activeProductFormId));

        if (matchingActiveRows.length === 0) {
            return;
        }

        const existingConnections = matchingActiveRows
            .map((r) => r.connection)
            .filter((c): c is number => c !== null && c !== undefined);

        const allSameConnection =
            existingConnections.length === matchingActiveRows.length && new Set(existingConnections).size === 1;

        let connectionId: number;
        const groupKey = this.getRowGroupKey(matchingActiveRows);

        if (allSameConnection) {
            connectionId = existingConnections[0];
        } else if (this.removedConnectionsByGroupKey[groupKey] !== undefined) {
            connectionId = this.removedConnectionsByGroupKey[groupKey];
            delete this.removedConnectionsByGroupKey[groupKey];
        } else {
            globalCombinationCounter += 1;
            connectionId = globalCombinationCounter;
        }

        this.activeItemRows.forEach((row: ProductRowItem) => {
            if (selectedIds.has(row.activeProductFormId)) {
                row.connection = connectionId;
            }
        });

        if (this.isAddingProducts) {
            const colors: Record<string | number, any> = { ...CustomColors };
            this.activeItemRows.forEach((productRowItem) => {
                setRowColor(productRowItem, colors);
            });
        }

        if (combinationImageBase64 && selectedRows.length > 0) {
            this.$set(this.combinationImages, String(connectionId), { image: combinationImageBase64, type });
        }
    }

    private async initCombinationCounter() {
        const existingItems = OfferItem.query().where('offer_id', this.offerId).get();

        const existingConnections = existingItems
            .map((item: any) => item.connection)
            .filter((c: any): c is number => c !== null && c !== undefined && typeof c === 'number');

        if (existingConnections.length > 0) {
            const max = Math.max(...existingConnections);
            if (max > globalCombinationCounter) {
                globalCombinationCounter = max;
            }
        }
    }

    private async mounted() {
        this.$el.addEventListener('paste', this.onPaste);
        EventBus.$on(EventBusEvents.addNewCombinationFromConfigurator, this.onConfiguratorCombinationEvent);
        this.setConfiguratorLevel();
        await this.initCombinationCounter();
    }

    private beforeDestroy() {
        this.$el.removeEventListener('paste', this.onPaste);
        EventBus.$off(EventBusEvents.addNewCombinationFromConfigurator, this.onConfiguratorCombinationEvent);
    }

    @Watch('availableSegments', { deep: true, immediate: true })
    private onAvailableSegmentsChange() {
        this.selectedSegment = this.availableSegments[0]?.value as string;
    }

    @Watch('selectedSegment', { deep: true })
    private async onSelectedSegmentChange() {
        this.debouncedUpdateHiddenIndicesInMultiposition();
        this.$emit('isLockedSegment', this.showCombinations);
        if (this.showCombinations && this.isWindowCombinationWizard) {
            this.switchLevel(2);
        } else {
            this.switchLevel(this.penultimateSelectedLevel);
        }

        if (this.showCombinations && this.count === 0) {
            this.count++;
        }
    }
}
